// Shared helpers for the retailer product and variation lists: both read
// GET /retailers/{id}/{products|variations} with the same query vocabulary
// and adapt the response to the hydra shape the list UI expects.

import {ITEM_PER_PAGE} from "config/_pagination";

// The list encodes id lists as ["a","b"] (brand/gender/options) or {a,b}
// (categories/channels); normalise both to plain ids.
const parseIds = (raw) => (raw || "")
    .replace(/^[[{]\s*|\s*[\]}]$/g, "")
    .split(",")
    .map((s) => s.trim().replace(/^"|"$/g, ""))
    .filter(Boolean);

// The retailer id is carried in the list params (retailer.id / retailer,
// possibly as an IRI).
export function parseListingParams(params) {
    const searchParams = new URLSearchParams(params);
    const rawRetailer = searchParams.get("retailer.id") || searchParams.get("retailer");

    return {
        searchParams,
        retailerId: rawRetailer ? rawRetailer.split("/").pop() : null,
        page: searchParams.get("page") || 1,
        itemsPerPage: searchParams.get("itemsPerPage") || ITEM_PER_PAGE,
    };
}

// Translate the list params (sort + filters) into the endpoint's plain
// query params. idFilters maps the list's id filter keys to the endpoint's
// (e.g. {"brand.id": "brand"} -> brand[]=<id>).
export function buildListingQuery({searchParams, page, itemsPerPage}, idFilters) {
    const query = new URLSearchParams();
    query.set("page", page);
    query.set("itemsPerPage", itemsPerPage);

    // Sort: forwarded as-is (order[field]=asc|desc).
    for (const [key, value] of searchParams.entries()) {
        if (key.startsWith("order[")) {
            query.append(key, value);
        }
    }

    Object.entries(idFilters).forEach(([rawKey, queryKey]) => {
        const raw = searchParams.get(rawKey);
        if (raw) {
            parseIds(raw).forEach((id) => query.append(`${queryKey}[]`, id));
        }
    });

    // Scalar filters (status / validated / stock / limited distribution)
    // + eshopId existence -> onEshop.
    ["status", "validated", "inStock", "limitedDistribution"].forEach((key) => {
        const value = searchParams.get(key);
        if (null !== value) {
            query.set(key, value);
        }
    });
    const eshopIdExists = searchParams.get("exists[eshopId]");
    if (null !== eshopIdExists) {
        query.set("onEshop", eshopIdExists);
    }

    // Price/Sales tab inputs: forwarded as-is (numeric, endpoint-side validated).
    ["priceMin", "priceMax", "discountMin", "discountMax", "salesMin"].forEach((key) => {
        const value = searchParams.get(key);
        if (null !== value && "" !== value) {
            query.set(key, value);
        }
    });

    // Scoring tab tokens (benchmark=higher|lower|equivalent,
    // demand=high|mid-high|mid-low|low): unknown values are ignored server-side.
    ["benchmark", "demand"].forEach((key) => {
        const value = searchParams.get(key);
        if (value) {
            query.set(key, value);
        }
    });

    // Lock filter: forwarded as-is (comma-separated field names, or
    // lock=all / lock=product / lock=variation).
    const lock = searchParams.get("lock");
    if (lock) {
        query.set("lock", lock);
    }

    // Free-text search: the list sends it as translations.name=<value>.
    const searchInput = searchParams.get("translations.name");
    if (searchInput) {
        query.set("search", searchInput);
    }

    return query;
}

// Translate the list params into the batch endpoint's search body (the
// merchandising SearchParams vocabulary): POST /retailers/{id}/products/batch
// resolves its targets from these filters server-side.
export function buildBatchActionSearchBody(params) {
    const searchParams = new URLSearchParams(params);
    const body = {};

    const idFilters = {
        "brand.id": "filterBrands",
        "categories.id": "filterCategories",
        "gender.id": "filterGenders",
        "channels.id": "filterChannels",
        "options.id": "filterOptionValues",
    };
    Object.entries(idFilters).forEach(([rawKey, bodyKey]) => {
        const raw = searchParams.get(rawKey);
        if (raw) {
            body[bodyKey] = parseIds(raw);
        }
    });

    const status = searchParams.get("status");
    if (null !== status) {
        body.filterStatus = "true" === status || "1" === status;
    }
    const validated = searchParams.get("validated");
    if (null !== validated) {
        body.filterValidated = "true" === validated || "1" === validated;
    }
    const eshopIdExists = searchParams.get("exists[eshopId]");
    if (null !== eshopIdExists) {
        body.filterOnEshop = "true" === eshopIdExists || "1" === eshopIdExists;
    }
    const inStock = searchParams.get("inStock");
    if (null !== inStock) {
        body.filterInStock = "true" === inStock || "1" === inStock;
    }
    const limitedDistribution = searchParams.get("limitedDistribution");
    if (null !== limitedDistribution) {
        body.filterLimitedDistribution = "true" === limitedDistribution || "1" === limitedDistribution;
    }

    const numericBodyKeys = {
        priceMin: "filterPriceMin",
        priceMax: "filterPriceMax",
        discountMin: "filterDiscountMin",
        discountMax: "filterDiscountMax",
        salesMin: "filterSalesMin",
    };
    Object.entries(numericBodyKeys).forEach(([rawKey, bodyKey]) => {
        const value = searchParams.get(rawKey);
        if (null !== value && "" !== value && !isNaN(Number(value))) {
            body[bodyKey] = Number(value);
        }
    });

    const benchmark = searchParams.get("benchmark");
    if (benchmark) {
        body.filterBenchmark = benchmark;
    }
    const demand = searchParams.get("demand");
    if (demand) {
        body.filterDemand = demand;
    }

    const lock = searchParams.get("lock");
    if (lock) {
        body.filterLocks = lock.split(",");
    }

    const searchInput = searchParams.get("translations.name");
    if (searchInput) {
        body.searchInput = searchInput;
    }

    return body;
}

// The shared pagination component builds front URLs from hydra:view,
// assuming the "<basePath>?<query>" shape (it keeps the query and re-embeds
// it in the front route). The retailer endpoints have a different path and
// no hydra:view, so synthesise one in that shape, preserving the original
// params (retailer.id, filters, order) and only moving page. Members are
// mapped through mapItem to the shape the list UI expects.
export function adaptListingResponse(retrieved, {params, basePath, page, itemsPerPage, mapItem}) {
    if (!Array.isArray(retrieved["hydra:member"])) {
        return retrieved;
    }

    const totalItems = retrieved["hydra:totalItems"] ?? 0;
    const perPage = parseInt(itemsPerPage, 10) || ITEM_PER_PAGE;
    const currentPage = parseInt(page, 10) || 1;
    const lastPage = Math.max(1, Math.ceil(totalItems / perPage));

    const viewUrl = (targetPage) => {
        const sp = new URLSearchParams(params);
        sp.set("page", targetPage);

        return basePath + "?" + sp.toString();
    };

    const view = {
        "@id": viewUrl(currentPage),
        "@type": "hydra:PartialCollectionView",
        "hydra:first": viewUrl(1),
        "hydra:last": viewUrl(lastPage),
    };
    if (currentPage > 1) {
        view["hydra:previous"] = viewUrl(currentPage - 1);
    }
    if (currentPage < lastPage) {
        view["hydra:next"] = viewUrl(currentPage + 1);
    }

    return {
        ...retrieved,
        "hydra:member": retrieved["hydra:member"].map(mapItem),
        "hydra:view": view,
    };
}
